import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Box,
  Divider
} from '@mui/material';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS
} from '../constants/formOptions';
import { FormValidationState, FormErrorState } from '../types/candidate.types';
import {
  LabelStyle,
  TextFieldStyle,
  selectStyle,
  sectionHeaderStyles,
  sectionTitleBoxStyles,
  errorTextStyles,
  sectionIconStyles,
  sectionTitleStyles
} from '../styles/CandidateForm.styles';
import PersonIcon from '@mui/icons-material/Person';

interface BasicInformationProps {
  register: any;
  isViewMode: boolean;
  isCreateEmsUserMode?: boolean;
  firstPageValidation: FormValidationState;
  setFirstPageValidation: (
    value:
      | FormValidationState
      | ((prev: FormValidationState) => FormValidationState)
  ) => void;
  selectedGender: string;
  setSelectedGender: (value: string) => void;
  selectedMaritalStatus: string;
  setSelectedMaritalStatus: (value: string) => void;
  firstErrors: FormErrorState;
  clearError: (section: 'first' | 'second' | 'third', errorKey: string) => void;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  register,
  isViewMode,
  isCreateEmsUserMode = false,
  firstPageValidation,
  setFirstPageValidation,
  selectedGender,
  setSelectedGender,
  selectedMaritalStatus,
  setSelectedMaritalStatus,
  firstErrors,
  clearError
}) => {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 1.5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: '#e3f2fd',
              color: '#1565c0'
            }}
          >
            <PersonIcon fontSize="small" />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#1565c0',
              fontSize: '1.1rem'
            }}
          >
            Basic Information
          </Typography>
        </Box>
        <Divider
          sx={{
            flex: 1,
            borderColor: '#1565c0',
            borderWidth: '0.5px'
          }}
        />
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            First Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            size="small"
            {...register('firstName')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter First Name"
            variant="outlined"
            disabled={isViewMode}
            value={firstPageValidation.firstName || ''}
            onChange={(e) => {
              const value = e.target.value;

              // Block input if length exceeds 50 characters
              if (value.length > 50) {
                return;
              }

              const allowedCharsRegex = /^[a-zA-Z\s'-]*$/;
              if (allowedCharsRegex.test(value)) {
                setFirstPageValidation((prev) => ({
                  ...prev,
                  firstName: value
                }));

                if (firstErrors?.firstNameError) {
                  clearError('first', 'firstNameError');
                }
              }
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              setFirstPageValidation((prev) => ({
                ...prev,
                firstName: trimmedValue
              }));
            }}
          />
          {firstErrors?.firstNameError && (
            <Typography sx={errorTextStyles} color="red">
              {firstPageValidation.firstName === '' ||
              firstPageValidation.firstName == null
                ? 'First Name is Required'
                : firstErrors?.firstNameError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Last Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            size="small"
            {...register('lastName')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter Last Name"
            variant="outlined"
            disabled={isViewMode}
            value={firstPageValidation.lastName || ''}
            onChange={(e) => {
              const value = e.target.value;

              if (value.length > 50) {
                return;
              }

              const allowedCharsRegex = /^[a-zA-Z\s'-]*$/;
              if (allowedCharsRegex.test(value)) {
                setFirstPageValidation((prev) => ({
                  ...prev,
                  lastName: value
                }));

                if (firstErrors?.lastNameError) {
                  clearError('first', 'lastNameError');
                }
              }
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              setFirstPageValidation((prev) => ({
                ...prev,
                lastName: trimmedValue
              }));
            }}
          />
          {firstErrors?.lastNameError && (
            <Typography sx={errorTextStyles} color="red">
              {firstPageValidation.lastName === '' ||
              firstPageValidation.lastName == null
                ? 'Last Name is Required'
                : firstErrors?.lastNameError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            NIC {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('nic')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter NIC (e.g., 959903239V)"
            variant="outlined"
            disabled={isViewMode}
            value={firstPageValidation.nic || ''}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();

              const nicRegex = /^[0-9V]*$/;
              if (!nicRegex.test(value)) {
                return;
              }

              if (value.length > 12) {
                return;
              }

              const vCount = (value.match(/V/g) || []).length;
              if (vCount > 1) {
                return;
              }

              if (value.includes('V') && value.length > 10) {
                return;
              }

              if (
                value.includes('V') &&
                value.length === 10 &&
                !value.endsWith('V')
              ) {
                return;
              }

              setFirstPageValidation((prev) => ({
                ...prev,
                nic: value
              }));

              if (firstErrors?.nicError) {
                clearError('first', 'nicError');
              }
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim().toUpperCase();
              setFirstPageValidation((prev) => ({
                ...prev,
                nic: trimmedValue
              }));
            }}
          />
          {firstErrors?.nicError && (
            <Typography sx={errorTextStyles} color="red">
              {firstPageValidation.nic === '' || firstPageValidation.nic == null
                ? 'NIC is Required'
                : firstErrors?.nicError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Date of Birth
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="date"
            {...register('dateOfBirth')}
            sx={TextFieldStyle}
            fullWidth
            variant="outlined"
            disabled={isViewMode}
            value={firstPageValidation.dateOfBirth || ''}
            inputProps={{
              min: new Date(
                new Date().setFullYear(new Date().getFullYear() - 100)
              )
                .toISOString()
                .split('T')[0],
              max: new Date(
                new Date().setFullYear(new Date().getFullYear() - 16)
              )
                .toISOString()
                .split('T')[0]
            }}
            error={
              Boolean(firstErrors?.dateOfBirthError) ||
              (isCreateEmsUserMode &&
                (firstPageValidation.dateOfBirth === '' ||
                  firstPageValidation.dateOfBirth == null))
            }
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              const minDate = new Date(
                new Date().setFullYear(new Date().getFullYear() - 100)
              );
              const maxDate = new Date(
                new Date().setFullYear(new Date().getFullYear() - 16)
              );

              if (selectedDate >= minDate && selectedDate <= maxDate) {
                setFirstPageValidation((prev) => ({
                  ...prev,
                  dateOfBirth: e.target.value
                }));
                if (firstErrors?.dateOfBirthError) {
                  clearError('first', 'dateOfBirthError');
                }
              } else {
                setFirstPageValidation((prev) => ({
                  ...prev,
                  dateOfBirth: e.target.value
                }));
              }
            }}
          />
          {isCreateEmsUserMode &&
            (firstErrors?.dateOfBirthError ||
              firstPageValidation.dateOfBirth === '' ||
              firstPageValidation.dateOfBirth == null) && (
              <Typography sx={errorTextStyles} color="red">
                {firstPageValidation.dateOfBirth === '' ||
                firstPageValidation.dateOfBirth == null
                  ? 'Date of Birth is Required'
                  : firstErrors?.dateOfBirthError}
              </Typography>
            )}
          {!isCreateEmsUserMode && firstErrors?.dateOfBirthError && (
            <Typography sx={errorTextStyles} color="red">
              {firstErrors?.dateOfBirthError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Gender{' '}
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <FormControl fullWidth>
            <Select
              size="small"
              sx={selectStyle}
              displayEmpty
              value={selectedGender || ''}
              disabled={isViewMode}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== '') {
                  setSelectedGender(value);
                  setFirstPageValidation((prev) => ({
                    ...prev,
                    gender: value
                  }));
                  if (firstErrors?.genderError) {
                    clearError('first', 'genderError');
                  }
                }
              }}
              renderValue={(selected) =>
                selected ? (
                  GENDER_OPTIONS.find((opt) => opt.value === selected)?.label
                ) : (
                  <span style={{ color: '#aaa' }}>Select Gender</span>
                )
              }
            >
              <MenuItem value="" disabled>
                Select Gender
              </MenuItem>
              {GENDER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {firstErrors?.genderError && (
            <Typography sx={errorTextStyles} color="red">
              {selectedGender === '' || selectedGender == null
                ? 'Gender is Required'
                : firstErrors?.genderError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Marital Status{' '}
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <FormControl fullWidth>
            <Select
              size="small"
              sx={selectStyle}
              displayEmpty
              value={selectedMaritalStatus || ''}
              disabled={isViewMode}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== '') {
                  setSelectedMaritalStatus(value);
                  setFirstPageValidation((prev) => ({
                    ...prev,
                    maritalStatus: value
                  }));
                  if (firstErrors?.maritalStatusError) {
                    clearError('first', 'maritalStatusError');
                  }
                }
              }}
              renderValue={(selected) =>
                selected ? (
                  MARITAL_STATUS_OPTIONS.find((opt) => opt.value === selected)
                    ?.label
                ) : (
                  <span style={{ color: '#aaa' }}>Select Marital Status</span>
                )
              }
            >
              <MenuItem value="" disabled>
                Select Marital Status
              </MenuItem>
              {MARITAL_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {firstErrors?.maritalStatusError && (
            <Typography sx={errorTextStyles} color="red">
              {selectedMaritalStatus === '' || selectedMaritalStatus == null
                ? 'Marital Status is Required'
                : firstErrors?.maritalStatusError}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default BasicInformation;
